import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import "./SignupForm.scss";
import TermsAndConditions from "../TermsAndConditions/TermsAndConditions";
import Overlay from "../Overlay/Overlay";
import passwordShowIcon from "../../assets/show-icon.png";
import passwordHideIcon from "../../assets/hide-icon.png";
const SignupForm = ({
  prepopulatedEmail,
  onLoginSubmit,
  onForgotPasswordSubmit,
  group,
  userValidated,
  isLogin,
  loginError,
  onSignupSubmit,
  resetLoginError,
  isLinkExpired,
  useMagicLink = false,
}) => {
  const [email, setEmail] = useState(prepopulatedEmail || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [termsChecked, setTermsChecked] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [isSignupClicked, setIsSignupClicked] = useState(false);
  const newErrorMessages = [];
  const [errorMessages, setErrorMessages] = useState([]);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [showMagicLinkTerms, setShowMagicLinkTerms] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  // Determine button text based on flow
  let btnText = isLogin ? (useMagicLink ? "Continue" : "Log In") : "Sign Up";
  const isResetFlow = window.location.href.indexOf("reset") > -1;
  if (isResetFlow) {
    btnText = "Reset Password";
  }
  const [buttonText, setButtonText] = useState(btnText);

  // Determine header text
  let headerText = isLogin ? "Log In" : "Sign Up";
  if (isResetFlow) {
    headerText = "Reset Password";
  } else if (isForgotPassword) {
    headerText = "Reset Password";
  }

  const handleCheckboxChange = () => {
    setTermsChecked(!termsChecked);
  };

  const handleTermsClick = () => {
    setShowTermsModal(true);
  };

  const closeTermsModal = () => {
    setShowTermsModal(false);
  };

  const validatePassword = (inputPassword) => {
    const minLength = 6;
    const hasUppercase = /[A-Z]/.test(inputPassword);
    const hasLowercase = /[a-z]/.test(inputPassword);

    if (inputPassword.length < minLength || !hasUppercase || !hasLowercase) {
      return "Password must have at least 6 characters, atleast one uppercase letter and one lowercase letter.";
    }

    return "";
  };

  const handlePasswordChange = () => {
    const error = validatePassword(password);
    setPasswordError(error);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true); // start processing

    if (!isForgotPassword) {
      if (isLogin) {
        const newErrorMessages = [];
        if (!email.trim()) newErrorMessages.push("Email cannot be empty.");
        if (!useMagicLink && !password.trim()) newErrorMessages.push("Password cannot be empty.");
        setErrorMessages(newErrorMessages);
        if (newErrorMessages.length >= 1) {
          setIsProcessing(false);
          return;
        }

        const result = await onLoginSubmit({ email, password, rememberMe });

        if (useMagicLink && result?.success) {
          setMagicLinkSent(true);
          setMagicLinkEmail(email);
        }
      } else {
        setIsSignupClicked(true);
        handlePasswordChange();

        const newErrorMessages = [];
        if (!password.trim()) newErrorMessages.push("Password cannot be empty.");
        if (passwordError) newErrorMessages.push(passwordError);
        if (!confirmPassword.trim()) newErrorMessages.push("Confirm Password cannot be empty.");
        if (password !== confirmPassword) newErrorMessages.push("Password and Confirm Password must match.");
        setErrorMessages(newErrorMessages);

        if (newErrorMessages.length >= 1 || !termsChecked) {
          setIsProcessing(false);
          return;
        }

        await onSignupSubmit({ email, password, isResetFlow });
      }
    }

    setIsProcessing(false); // stop processing
  };
  const handleForgotPasswordClick = () => {
    setEmail("");
    setErrorMessages([]);
    resetLoginError(null);
    setButtonText("Reset Password");
    setIsForgotPassword(true);
  };

  const handleBackToLogin = () => {
    setEmail("");
    resetLoginError(null);
    const btnTxt = isLogin ? (useMagicLink ? "Continue" : "Log In") : "Sign Up";
    setButtonText(btnTxt);
    setErrorMessages([]);
    setIsForgotPassword(false);
    setResetPasswordSuccess(false);
    setMagicLinkSent(false);
  };

  const handleResetPasswordSubmit = async (e) => {
    setErrorMessages([]);
    e.preventDefault();
    const result = await onForgotPasswordSubmit(email);
    if (result.success) {
      setResetPasswordSuccess(true);
    } else {
      newErrorMessages.push(
        "The email address you entered is incorrect. Please review your input and try again."
      );
      setErrorMessages(newErrorMessages);
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const isSignupButtonDisabled = () => {
    if (isForgotPassword || resetPasswordSuccess || magicLinkSent) {
      return true;
    }

    if (isLogin) {
      if (useMagicLink) {
        return !termsChecked;
      }
      return false;
    } else {
      return !termsChecked;
    }
  };

  useEffect(() => {
    isSignupButtonDisabled();
  }, [
    email,
    password,
    confirmPassword,
    termsChecked,
    isLogin,
    isForgotPassword,
    resetPasswordSuccess,
    magicLinkSent
  ]);

  useEffect(() => {
    if (isLinkExpired) {
      window.location.href = `login?group=${group?.toLowerCase()}`;
    }
  }, [isLinkExpired, group]);

  // Render the magic link sent confirmation
  if (magicLinkSent) {
    return (
      <div className={`front-page__login ${group?.toLowerCase()}-bg`}>
        <div style={{
          backgroundColor: "hsla(0, 0%, 100%, 0.9)",
          position: "relative",
          zIndex: 5,
          padding: "10px",
          borderRadius: "5px",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.15)",
          maxWidth: "400px",
          width: "100%",
          textAlign: "center",
          margin: "0 auto",
          border: "1px solid rgba(255, 255, 255, 0.3)"
        }}>
          {!magicLinkSent && (
            <header className="wellness-card__header">
              <h2>Log In</h2>
            </header>
          )}

          <div style={{ marginTop: '20px' }}>
            <img
              src="https://wrapublicfiles.s3.ca-central-1.amazonaws.com/myheartcoach-logo.png"
              alt="Wellness Toolbox"
              style={{
                maxWidth: '180px',
                height: 'auto'
              }}
            />
          </div>

          <div style={{
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            borderRadius: "8px",
            padding: "10px",
            textAlign: "center",
            margin: "0 auto",
            border: "1px solid rgba(255, 255, 255, 0.3)"
          }}>
            <p style={{ marginBottom: '15px' }}>
              We've sent a verification link to {magicLinkEmail}.
            </p>
            <p>Click the link in your email to securely log in.</p>
            <p style={{ color: "#6028bf", fontWeight: "500", fontSize: "16px" }}>
              If you don't see our email, please check your Junk/Spam folder.
            </p>
            <div style={{
              marginTop: "24px",
              textAlign: "center"
            }}>
              <p style={{ fontWeight: "500" }}>
                Need help? Contact Support at
              </p>
              <p style={{ color: "#6028bf", fontWeight: "500", fontSize: "16px" }}>
                587-341-4042
              </p>
              <p style={{ fontWeight: "500" }}>
                (Monday-Friday 8am-5pm)
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render forgot password success
  if (resetPasswordSuccess) {
    return (
      <div className={`front-page__login ${group?.toLowerCase()}-bg`}>
        <div style={{ width: '100%', maxWidth: '400px' }}>
          <header className="wellness-card__header">
            <h2>Reset Password</h2>
          </header>

          <p>
            Password reset email has been sent. Please check your email
            inbox.
          </p>

          <div
            className="back-to-login-btn back-to-login-btn__reset"
            onClick={handleBackToLogin}
          >
            Back to Login
          </div>
        </div>
      </div>
    );
  }

  // Main form render
  return (
    <div className={`front-page__login ${group?.toLowerCase()}-bg`}>
      {userValidated || isLogin ? (
        <form
          style={{
            width: '100%',
            maxWidth: '500px',
            padding: '32px',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}
          onSubmit={isForgotPassword ? handleResetPasswordSubmit : handleSubmit}
        >
          {useMagicLink && (
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <img
                src='https://wrapublicfiles.s3.ca-central-1.amazonaws.com/myheartcoach-logo.png'
                alt="Wellness Toolbox"
                style={{
                  maxWidth: '60%',
                  height: 'auto'
                }}
              />
            </div>
          )}

          <header className="wellness-card__header" style={{ marginBottom: '24px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#000' }}>{headerText}</h2>
          </header>

          {useMagicLink && (
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <p style={{ color: '#000', fontSize: '16px', fontWeight: '500' }}>Enter your email address to access your program</p>
            </div>
          )}

          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label htmlFor="user_login" style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '500',
              color: '#333'
            }}>Email Address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              readOnly={!!prepopulatedEmail}
              size="20"
              autoComplete="off"
              className={!isLogin && "form-group__email"}
              style={{
                padding: '12px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '16px'
              }}
            />
          </div>

          {useMagicLink && (
            <div className="form-group form-group__terms" style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="magicLinkTermsCheckbox"
                  checked={termsChecked}
                  onChange={handleCheckboxChange}
                  style={{ marginTop: '4px' }}
                />
                <label htmlFor="magicLinkTermsCheckbox" className="terms-label" style={{ fontSize: '14px', color: '#666' }}>
                  I have read and agree to the{" "}
                  <span
                    className="terms-link"
                    onClick={() => setShowMagicLinkTerms(true)}
                    style={{ cursor: 'pointer', color: '#6028bf', textDecoration: 'underline' }}
                  >
                    Terms of Use
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Magic Link Terms Modal */}
          {showMagicLinkTerms && (
            <>
              <Overlay onClick={() => setShowMagicLinkTerms(false)} />
              <div className="terms-modal" style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'white',
                padding: '20px',
                borderRadius: '8px',
                zIndex: 1000,
                maxWidth: '500px',
                width: '90%',
                maxHeight: '80vh',
                overflowY: 'auto'
              }}>
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ marginBottom: '15px' }}>Terms of Use</h3>
                  <p>This digital platform and its contents:</p>
                  <ol>
                    <li>are research based in nature</li>
                    <li>do not represent medical/clinical care, and</li>
                    <li>should not be construed or relied upon as a replacement or substitute for medical/clinical care or medical advice form a qualified healthcare professional, and that the University of Alberta and this platform's researchers expressly disclaim all liability for the user of this digital platform in this study.</li>
                  </ol>
                </div>
                <button
                  onClick={() => setShowMagicLinkTerms(false)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#6028bf',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </>
          )}

          {/* Only show password field if not using magic link and not in forgot password flow */}
          {!isForgotPassword && !useMagicLink && (
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label htmlFor="password" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: '#333'
              }}>Password</label>
              <div className="password-div" style={{ position: 'relative' }}>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    fontSize: '16px',
                    paddingRight: '40px'
                  }}
                />
                <div
                  className="password-toggle-icon tooltip"
                  onClick={() => setShowPassword(!showPassword)}
                  title={showPassword ? "Hide Password" : "Show Password"}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer'
                  }}
                >
                  {!showPassword ? (
                    <img
                      src={passwordShowIcon}
                      alt="show password"
                      height={20}
                    />
                  ) : (
                    <img
                      src={passwordHideIcon}
                      alt="show password"
                      height={20}
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {!isLogin && !useMagicLink && (
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label htmlFor="confirmPassword" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: '#333'
              }}>Confirm Password</label>
              <div className="password-div" style={{ position: 'relative' }}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    fontSize: '16px',
                    paddingRight: '40px'
                  }}
                />
                <div
                  className="password-toggle-icon tooltip"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  title={showConfirmPassword ? "Hide Password" : "Show Password"}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer'
                  }}
                >
                  {!showConfirmPassword ? (
                    <img
                      src={passwordShowIcon}
                      alt="show password"
                      height={20}
                    />
                  ) : (
                    <img
                      src={passwordHideIcon}
                      alt="show password"
                      height={20}
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {!isLogin && (
            <div
              className={`form-group form-group__terms ${isSignupClicked && !termsChecked && "terms-error"}`}
              style={{ marginBottom: '24px' }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="termsCheckbox"
                  checked={termsChecked}
                  onChange={handleCheckboxChange}
                  style={{ marginTop: '4px' }}
                />
                <label htmlFor="termsCheckbox" className="terms-label" style={{ fontSize: '14px', color: '#666' }}>
                  I have read and agree to the{" "}
                  <span className="terms-link" onClick={handleTermsClick} style={{ color: '#6028bf', textDecoration: 'underline', cursor: 'pointer' }}>
                    Terms Of Use
                  </span>
                </label>
              </div>
            </div>
          )}

          {errorMessages.length > 0 && (
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <div className="terms-error">
                {errorMessages.map((message, index) => (
                  <p key={index} className="error-message" style={{ color: '#dc3545', fontSize: '14px', marginBottom: '8px' }}>
                    {message}
                  </p>
                ))}
              </div>
            </div>
          )}

          {!isForgotPassword && loginError && (
            <div className="form-group form-login" style={{ marginBottom: '20px' }}>
              <div className="terms-error">
                <p className="error-message" style={{ color: '#dc3545', fontSize: '14px' }}>{loginError}</p>
              </div>
            </div>
          )}

          {isLogin && !isForgotPassword && !useMagicLink && (
            <div className="form-group form-group__remember-me" style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="rememberMeCheckbox"
                  checked={rememberMe}
                  onChange={() => setRememberMe(!rememberMe)}
                />
                <label htmlFor="rememberMeCheckbox" className="remember-me-label" style={{ fontSize: '14px', color: '#666' }}>
                  Remember Me
                </label>
              </div>
            </div>
          )}

          {isForgotPassword ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <button
                type="submit"
                className="reset-pwd-btn"
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#6028bf',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Reset Password
              </button>
              <div
                className="back-to-login-btn"
                onClick={handleBackToLogin}
                style={{
                  textAlign: 'center',
                  color: '#6028bf',
                  cursor: 'pointer',
                  fontSize: '14px',
                  textDecoration: 'underline'
                }}
              >
                Back to Login
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <button
                type="submit"
                disabled={isSignupButtonDisabled() || isProcessing}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#6028bf',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: isSignupButtonDisabled() || isProcessing ? 'not-allowed' : 'pointer',
                  opacity: isSignupButtonDisabled() || isProcessing ? 0.7 : 1
                }}
              >
                {isProcessing ? "Processing..." : btnText}
              </button>
              {isLogin && !useMagicLink && (
                <div
                  className="forgot-password-link"
                  onClick={handleForgotPasswordClick}
                  style={{
                    textAlign: 'center',
                    color: '#6028bf',
                    cursor: 'pointer',
                    fontSize: '14px',
                    textDecoration: 'underline'
                  }}
                >
                  Forgot Password?
                </div>
              )}
            </div>
          )}

          {showTermsModal && <Overlay onClick={closeTermsModal} />}
          {showTermsModal && (
            <div className="terms-modal">
              <TermsAndConditions onClose={closeTermsModal} />
            </div>
          )}
        </form>
      ) : (
        <p>User does not exist</p>
      )}
    </div>
  );
};

SignupForm.propTypes = {
  prepopulatedEmail: PropTypes.string,
  onLoginSubmit: PropTypes.func.isRequired,
  onForgotPasswordSubmit: PropTypes.func.isRequired,
  group: PropTypes.string,
  userValidated: PropTypes.bool,
  isLogin: PropTypes.bool,
  loginError: PropTypes.string,
  onSignupSubmit: PropTypes.func,
  resetLoginError: PropTypes.func,
  isLinkExpired: PropTypes.bool,
  useMagicLink: PropTypes.bool
};

export default SignupForm;